// Hebrew aleph-bet → Ahavah Rabbah (Phrygian Dominant) scale across 3 octaves.
// Supports:
//   • 22 base letters + final forms (ך ם ן ף ץ collapse to base)
//   • 3 alternate letterforms: vet (בֿ), sin (שׂ), sav (תֿ) — same notes as ב, ש, ת
//   • 12 chromatic roots × 7 modal rotations of Ahavah Rabbah

const ALEF_BET = [
  "א", "ב", "ג", "ד", "ה", "ו", "ז", "ח", "ט", "י",
  "כ", "ל", "מ", "נ", "ס", "ע", "פ", "צ", "ק", "ר",
  "ש", "ת",
];

const FINAL_FORMS = {
  "ך": "כ",
  "ם": "מ",
  "ן": "נ",
  "ף": "פ",
  "ץ": "צ",
};

// Hebrew combining marks we strip when normalising for note lookup.
// (rafe ◌ֿ U+05BF, dagesh ◌ּ U+05BC, sin dot ◌ׂ U+05C2, shin dot ◌ׁ U+05C1)
const COMBINING_MARKS = ["\u05BF", "\u05BC", "\u05C2", "\u05C1"];

// Multi-codepoint alternate forms exposed on the keyboard.
//   vet → ב + dagesh ◌ּ (U+05BC) → same note as ב
//   sin → ש + sin-dot ◌ׂ (U+05C2) → same note as ש
//   sav → ת + dagesh ◌ּ (U+05BC) → same note as ת
export const HEBREW_VARIANTS = {
  vet: "\u05D1\u05BC",
  sin: "\u05E9\u05C2",
  sav: "\u05EA\u05BC",
};

// Ahavah Rabbah / Phrygian Dominant interval pattern (semitones above root)
const AHAVAH_RABBAH_INTERVALS = [0, 1, 4, 5, 7, 8, 10];
// Diatonic major scale — used by the homepage mini-app so the preview always
// sounds friendly to a casual visitor.
export const MAJOR_INTERVALS = [0, 2, 4, 5, 7, 9, 11];
// Natural minor.
export const MINOR_INTERVALS = [0, 2, 3, 5, 7, 8, 10];

// The three scale families the studio offers. Ahavah Rabbah is the default and
// the one every saved piece predates this choice was written in.
export const SCALES = [
  { value: "ahavah_rabbah", label: "Ahavah Rabbah", intervals: AHAVAH_RABBAH_INTERVALS },
  { value: "major", label: "Major", intervals: MAJOR_INTERVALS },
  { value: "minor", label: "Minor", intervals: MINOR_INTERVALS },
];

export const DEFAULT_SCALE = "ahavah_rabbah";

export function getScaleConfig(value) {
  return SCALES.find((s) => s.value === value) || SCALES[0];
}

export function intervalsForScale(value) {
  return getScaleConfig(value).intervals;
}

const LETTERS = ["C", "D", "E", "F", "G", "A", "B"];
const LETTER_SEMITONES = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

// 12 chromatic roots. Each one defines the pitch class (pc) and the preferred
// letter spelling for the root note. The letter choice biases the whole scale's
// spelling so each degree gets a unique letter where possible.
export const ROOTS = [
  { value: "C",  label: "C",  pc: 0,  letter: "C", accidental: null },
  { value: "Db", label: "D\u266D", pc: 1, letter: "D", accidental: "b" },
  { value: "D",  label: "D",  pc: 2,  letter: "D", accidental: null },
  { value: "Eb", label: "E\u266D", pc: 3, letter: "E", accidental: "b" },
  { value: "E",  label: "E",  pc: 4,  letter: "E", accidental: null },
  { value: "F",  label: "F",  pc: 5,  letter: "F", accidental: null },
  { value: "F#", label: "F\u266F", pc: 6, letter: "F", accidental: "#" },
  { value: "G",  label: "G",  pc: 7,  letter: "G", accidental: null },
  { value: "Ab", label: "A\u266D", pc: 8, letter: "A", accidental: "b" },
  { value: "A",  label: "A",  pc: 9,  letter: "A", accidental: null },
  { value: "Bb", label: "B\u266D", pc: 10, letter: "B", accidental: "b" },
  { value: "B",  label: "B",  pc: 11, letter: "B", accidental: null },
];

export const DEFAULT_ROOT = "G";
export const DEFAULT_MODE = 0;

// Rotate a scale's intervals to start on the given mode degree (0..6).
function modePattern(modeIdx, intervals = AHAVAH_RABBAH_INTERVALS) {
  const m = modeIdx % 7;
  const offset = intervals[m];
  const out = [];
  for (let i = 0; i < 7; i += 1) {
    const v = (intervals[(m + i) % 7] - offset + 12) % 12;
    out.push(v);
  }
  return out;
}

// Mode labels are computed per-root by callers (they need the actual note
// names of the scale). We just expose the seven mode indices.
export const MODES = [0, 1, 2, 3, 4, 5, 6].map((i) => ({ value: i, label: `Mode ${i + 1}` }));

// The 7 note names of [root] [scale], e.g.
//   notesForScale("D") -> ["D", "E♭", "F♯", "G", "A", "B♭", "C"]
// (the scale itself never changes with mode; only where alef sits on it does.)
export function notesForScale(rootValue, scaleValue = DEFAULT_SCALE) {
  const rootCfg = getRootConfig(rootValue);
  const intervals = intervalsForScale(scaleValue);
  return [0, 1, 2, 3, 4, 5, 6].map((deg) => {
    const { letter, accidental } = spellDegree(rootCfg, intervals, deg);
    const acc = accidental === "#" ? "\u266F" : accidental === "b" ? "\u266D" : "";
    return `${letter}${acc}`;
  });
}

// Which note name is alef on for a given root + mode?
// Mode N (0-indexed) means alef sits on the (N+1)-th scale degree.
export function alefNoteForMode(rootValue, modeIdx, scaleValue = DEFAULT_SCALE) {
  const notes = notesForScale(rootValue, scaleValue);
  return notes[modeIdx % 7];
}

// Map a root.value -> root config object.
function getRootConfig(value) {
  return ROOTS.find((r) => r.value === value) || ROOTS.find((r) => r.value === DEFAULT_ROOT);
}

// Resolve scale degree -> { letter, accidental, octaveShift } in a given key.
// option 2b: if a true spelling would require a double accidental, fall back to
// an enharmonic single accidental (letter may repeat in the scale — that's OK).
function spellDegree(rootCfg, pattern, degree) {
  const targetPc = (rootCfg.pc + pattern[degree]) % 12;
  const rootLetterIdx = LETTERS.indexOf(rootCfg.letter);
  const preferredLetter = LETTERS[(rootLetterIdx + degree) % 7];
  const naturalPc = LETTER_SEMITONES[preferredLetter];
  const delta = ((targetPc - naturalPc) + 12) % 12;

  if (delta === 0) return { letter: preferredLetter, accidental: null, targetPc };
  if (delta === 1) return { letter: preferredLetter, accidental: "#", targetPc };
  if (delta === 11) return { letter: preferredLetter, accidental: "b", targetPc };

  // Would need double accidental — fall back to nearest single-accidental letter.
  for (const L of LETTERS) {
    const d = ((targetPc - LETTER_SEMITONES[L]) + 12) % 12;
    if (d === 0) return { letter: L, accidental: null, targetPc };
    if (d === 1) return { letter: L, accidental: "#", targetPc };
    if (d === 11) return { letter: L, accidental: "b", targetPc };
  }
  return { letter: preferredLetter, accidental: null, targetPc };
}

export const HEBREW_BASE_LETTERS = ALEF_BET;
export const HEBREW_FINAL_FORMS = FINAL_FORMS;

// Strip combining marks; collapse final forms.
export function normalizeHebrewLetter(ch) {
  if (!ch) return ch;
  let s = ch;
  // strip any combining nikud / dot marks
  for (const m of COMBINING_MARKS) s = s.split(m).join("");
  if (FINAL_FORMS[s]) return FINAL_FORMS[s];
  return s;
}

export function isHebrewLetter(ch) {
  if (!ch) return false;
  const base = normalizeHebrewLetter(ch);
  return ALEF_BET.indexOf(base) >= 0;
}

// Iterate a string as Hebrew "graphemes" — a base letter plus any trailing
// combining marks. Returns an array of { glyph, baseLetter } for letters only.
// Non-letter chars are skipped (callers should handle word boundaries upstream).
export function tokenizeHebrew(word) {
  if (!word) return [];
  const out = [];
  const chars = [...word];
  let i = 0;
  while (i < chars.length) {
    const c = chars[i];
    // Skip stand-alone combining marks (shouldn't happen normally).
    if (COMBINING_MARKS.indexOf(c) >= 0) {
      i += 1;
      continue;
    }
    let glyph = c;
    let j = i + 1;
    while (j < chars.length && COMBINING_MARKS.indexOf(chars[j]) >= 0) {
      glyph += chars[j];
      j += 1;
    }
    const base = normalizeHebrewLetter(glyph);
    if (ALEF_BET.indexOf(base) >= 0) {
      out.push({ glyph, baseLetter: base, startIdx: i });
    }
    i = j;
  }
  return out;
}

// The base octave for the root in mode 1 (rooted on the chosen root letter).
// We choose 2 so that the lowest pitches land in the bass clef.
const ROOT_BASE_OCTAVE = 2;

// `condensed` (studio-only, off by default): keep every letter inside a single
// octave, so the 22 letters cycle 1-7, 1-7, 1-7, 1 instead of climbing three
// octaves. The scale degree is unchanged — every letter is pinned to the middle
// octave of the three the full range normally covers, so it reads across the
// middle of the grand staff rather than down in the bass.
//
// Tav is the exception: as the 22nd letter it would restart the cycle on 1, so
// instead it closes it on 8 — the same note an octave up.
const CONDENSED_OCT_STEP = 1;
const TAV_IDX = ALEF_BET.length - 1;
//
// Map a Hebrew letter (with optional combining marks) to a staff note,
// given a { root, modeIdx }. The scale itself never changes — it is always
// the chosen root's Ahavah Rabbah / Phrygian Dominant scale. The `modeIdx`
// determines which scale degree alef starts on (0 = root, 1 = b2, …, 6 = b7).
// Bet is the next scale degree (mod 7), gimel the one after, etc., with
// the octave bumping each time the cycle wraps past degree 6.
//
// Returns { letter, baseLetter, note, accidental, octave, pitch, scaleDegree, semitones }
export function hebrewLetterToNote(letter, options = {}) {
  if (!isHebrewLetter(letter)) return null;
  const rootValue = options.root || DEFAULT_ROOT;
  const modeIdx = options.modeIdx ?? DEFAULT_MODE;
  // `intervals` (raw array) still wins so the marketing demos can pass MAJOR
  // directly; otherwise the named scale decides.
  const intervals = options.intervals || intervalsForScale(options.scale || DEFAULT_SCALE);
  // Demos raise this so a G-rooted phrase doesn't sit down in the bass.
  const baseOctave = options.baseOctave ?? ROOT_BASE_OCTAVE;
  const rootCfg = getRootConfig(rootValue);

  const base = normalizeHebrewLetter(letter);
  const idx = ALEF_BET.indexOf(base);
  if (idx < 0) return null;

  // alef sits at scale degree `modeIdx`. Each successive letter advances one
  // diatonic degree of the chosen root's Ahavah Rabbah scale.
  const absoluteDegree = modeIdx + idx;
  const scaleDegree = absoluteDegree % 7;
  const octStep = options.condensed
    ? CONDENSED_OCT_STEP + (idx === TAV_IDX ? 1 : 0)
    : Math.floor(absoluteDegree / 7);

  const { letter: noteLetter, accidental } = spellDegree(
    rootCfg,
    intervals,
    scaleDegree
  );

  const totalSemis =
    baseOctave * 12 +
    rootCfg.pc +
    intervals[scaleDegree] +
    octStep * 12;

  let accOff = 0;
  if (accidental === "#") accOff = 1;
  else if (accidental === "b") accOff = -1;
  const octave = Math.floor(
    (totalSemis - LETTER_SEMITONES[noteLetter] - accOff) / 12
  );

  const accStr = accidental || "";
  const pitch = `${noteLetter}${accStr}${octave}`;

  return {
    letter,
    baseLetter: base,
    note: noteLetter,
    accidental: accidental || null,
    octave,
    pitch,
    scaleDegree,
    semitones: intervals[scaleDegree] + octStep * 12,
  };
}
